import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import {
    EditButton,
    Show,
    useCreatePath,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import {
    LOCALE_NAMES,
    NEWS_LOCALES,
    NEWS_POSTS_RESOURCE,
    SOURCE_LOCALE,
    formatPeriod,
    textNotification,
} from '../common';
import { StatusChip, TranslationStatusChip } from '../chips';
import { LoadingLabel, errorOutlinedButtonSx, filledButtonSx, outlinedButtonSx } from '../buttons';
import DeleteNewsPostButton from '../DeleteNewsPostButton';
import { labelSx } from '../form/CharCounter';
import NewsPopupPreview, { PREVIEW_MAX_WIDTH } from '../preview/NewsPopupPreview';

// Mismas etiquetas que el formulario de edición (14 px, peso 500, sin mayúsculas) y el mismo
// espacio entre bloques (`gap: 2.5` en la tarjeta).
const Section = ({ title, children }) => (
    <Box>
        <Typography sx={{ ...labelSx, mb: 0.75 }}>{title}</Typography>
        {children}
    </Box>
);

/**
 * Una métrica del pop-up con lo que cuenta. Todas salen de la fila del pop-up de `news_post_user` (ver
 * `NewsPostUserRepository::findMetrics()` en hrbot_back); los enlaces del texto van aparte, en
 * `LinkClicks`.
 */
const Metric = ({ label, help, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14 }}>{label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                {help}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2 }}>{value ?? 0}</Typography>
    </Box>
);

export const RetranslateButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const { mutate, isLoading } = useMutation(() => dataProvider.translate(NEWS_POSTS_RESOURCE, { id: record.id }), {
        onSuccess: () => {
            notify(...textNotification('Traducciones actualizadas.', { type: 'success' }));
            refresh();
        },
        onError: (error) =>
            notify(...textNotification(error?.message || 'No se han podido actualizar las traducciones', { type: 'error', multiLine: true })),
    });

    // `isLoading` no cambia hasta el siguiente render: un doble clic llegaría a lanzar dos
    // peticiones. El ref cierra el hueco de forma síncrona; el estado visible sigue saliendo de
    // `isLoading` de la mutación.
    const inFlight = React.useRef(false);
    const retranslate = () => {
        if (inFlight.current) return;
        inFlight.current = true;
        mutate(undefined, {
            onSettled: () => {
                inFlight.current = false;
            },
        });
    };

    if (!record) return null;

    return (
        <Button variant="outlined" onClick={retranslate} disabled={isLoading} aria-label="Volver a traducir" aria-busy={isLoading} sx={outlinedButtonSx}>
            <LoadingLabel loading={isLoading}>
                <TranslateIcon fontSize="small" />
                Volver a traducir
            </LoadingLabel>
        </Button>
    );
};

/**
 * Enlaces del texto que han pulsado los usuarios, desde el pop-up o desde Novedades (revisión del
 * 28-oct-2026). Cada usuario cuenta una vez por enlace: se guarda qué pulsó, no cuántas veces.
 */
const LinkClicks = ({ links }) =>
    links.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
            Nadie ha pulsado todavía ningún enlace del texto.
        </Typography>
    ) : (
        <Stack spacing={1} data-testid="news-post-link-clicks">
            {links.map(({ url, users }) => (
                <Box key={url} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: 14, minWidth: 0, overflowWrap: 'anywhere', color: 'primary.main' }}>
                        {url}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {users} {users === 1 ? 'usuario' : 'usuarios'}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );

const NewsPostDetail = () => {
    const record = useRecordContext();
    const createPath = useCreatePath();
    const [locale, setLocale] = React.useState(SOURCE_LOCALE);

    if (!record) return null;

    // Misma distribución que el formulario de edición: contenedor centrado de 1440 px como máximo,
    // tarjeta a la izquierda (crecimiento 3) y vista previa a la derecha (crecimiento 2, 520 px como
    // máximo). Si cambia uno, cambiar el otro.
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1440, mx: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Button
                    component={Link}
                    to={createPath({ resource: NEWS_POSTS_RESOURCE, type: 'list' })}
                    size="small"
                    sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, fontWeight: 400 }}
                >
                    ← Volver al listado
                </Button>
                <Typography variant="h1" sx={{ fontSize: 26, fontWeight: 600, m: 0, wordBreak: 'break-word' }}>
                    {record.title?.es}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                <Paper variant="outlined" sx={{ flex: '3 1 520px', minWidth: 0, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Section title="Estado">
                        <StatusChip status={record.status} />
                    </Section>
                    <Section title="Vigencia">
                        <Typography sx={{ fontSize: 14 }}>{formatPeriod(record.starts_at, record.ends_at)}</Typography>
                    </Section>
                    <Section title="Métricas del pop-up">
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: -0.5 }}>
                            Sin contar a los usuarios de Velora (@velorahr.com y @hrbotfactory.com).
                        </Typography>
                        <Stack spacing={1.25} data-testid="news-post-metrics">
                            <Metric
                                label="Usuarios alcanzados"
                                help="Usuarios a los que se les mostró el pop-up al menos una vez."
                                value={record.metrics?.users_reached}
                            />
                            <Metric
                                label="Impresiones del pop-up"
                                help="Veces que se mostró el pop-up, como máximo 3 por usuario."
                                value={record.metrics?.impressions}
                            />
                            <Metric
                                label="Abrieron Novedades"
                                help="Usuarios que pulsaron «Ver todas las novedades» en el pop-up."
                                value={record.metrics?.clicks}
                            />
                        </Stack>
                    </Section>
                    <Section title="Enlaces pulsados">
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: -0.5, mb: 0.75 }}>
                            Usuarios que pulsaron cada enlace del texto, en el pop-up o en Novedades. Sin contar a los usuarios de Velora.
                        </Typography>
                        <LinkClicks links={record.link_clicks || []} />
                    </Section>
                    <Section title="Traducciones">
                        <Stack spacing={1} data-testid="translation-statuses">
                            {NEWS_LOCALES.filter((code) => code !== SOURCE_LOCALE).map((code) => (
                                <Box key={code} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: 14 }}>{LOCALE_NAMES[code]}</Typography>
                                    <TranslationStatusChip status={record.translation_status?.[code] || 'missing'} />
                                </Box>
                            ))}
                        </Stack>
                    </Section>
                    <Divider />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <EditButton label="Editar" variant="contained" sx={filledButtonSx} />
                        <RetranslateButton />
                        <DeleteNewsPostButton variant="outlined" sx={errorOutlinedButtonSx} />
                    </Stack>
                </Paper>
                <Box sx={{ flex: '2 1 380px', width: '100%', maxWidth: PREVIEW_MAX_WIDTH, minWidth: 0 }}>
                    <NewsPopupPreview
                        title={record.title}
                        body={record.body}
                        imageSrc={record.image_url}
                        startsAt={record.starts_at}
                        endsAt={record.ends_at}
                        locale={locale}
                        onLocaleChange={setLocale}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const NewsPostShow = () => (
    <Show title="Novedad" actions={false}>
        <NewsPostDetail />
    </Show>
);

export default NewsPostShow;
